// Normalizador de errores de la API (catalogo v7.0.5: TODOS los errores traen detail = { code, message, params }).
//
// Reglas (Producto + Seguridad + UI/UX):
//  - response.data.detail queda SIEMPRE como texto seguro para el usuario (o undefined), asi los ~210 lectores
//    `err.response?.data?.detail || 'fallback'` siguen funcionando y nunca pintan "[object Object]".
//  - El mensaje del backend NUNCA se muestra:
//      * codigo traducido en el catalogo i18n (ns 'errors', clave errors.<code>) -> esa traduccion;
//      * 4xx sin traduccion -> fallback de pantalla (o errors.unknown) + "Codigo de soporte: <request_id>" solo si
//        viene request_id; NUNCA el codigo crudo entre parentesis (se registra el codigo faltante solo en dev);
//      * CARD_* sin traduccion propia -> errors.card_generic_decline (rechazo amable);
//      * 5xx / INTERNAL_ERROR -> fallback i18n + "Codigo de soporte: <request_id>";
//      * 422 (lista FastAPI) -> "Revisa los campos marcados (n)"; detalle por campo con getFieldErrors();
//      * red -> errors.network_error.
//  - El objeto original, params y el message crudo se guardan FUERA del objeto de error (WeakMap), para que
//    console.log(err) / analytics nunca los vean. Solo se exponen data.code y error.errorCode (codigo, sin params).
// Modulo puro (sin axios ni i18next) para poder probarlo con node --test.

export type ErrorParams = Record<string, unknown>;
export type TLike = (key: string, opts?: Record<string, unknown>) => unknown;
export type ErrorKind = 'network' | 'server' | 'validation' | 'client' | 'unknown';
export type ErrorInfo = {
  kind: ErrorKind;
  status?: number;
  code?: string;
  requestId?: string;
  message: string;
  fieldErrors: Record<string, string>;
};

export const ERRORS_NS = 'errors';
export const MAX_ERROR_MESSAGE_LENGTH = 300;
const CODE_RE = /^[A-Z][A-Z0-9_]{1,80}$/;
const REQ_ID_RE = /^[A-Za-z0-9._:-]{1,100}$/;
// Header X-Request-ID: solo si es UUID. El hash numerico actual no coincide con logs (hasta que Backend unifique).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OBJ_STR = '[object Object]';

// Textos por defecto (el catalogo errors.json de T1 los sobreescribe cuando exista).
const DEFAULTS: Record<'es' | 'en', Record<string, string>> = {
  es: {
    'errors.unknown': 'Algo sali\u00f3 mal. Intenta de nuevo; si sigue pasando, contacta a soporte.',
    'errors.endpoint_gone': 'Esta funci\u00f3n ya no est\u00e1 disponible.',
    'errors.upload_session_expired': 'La sesi\u00f3n de subida venci\u00f3; vuelve a subir el archivo.',
    'errors.upload_init_failed': 'No se pudo iniciar la subida (HTTP {{status}})',
    'errors.upload_assembly_failed': 'No se pudo completar la subida en el servidor (HTTP {{status}})',
    'errors.addon_not_active_generic': 'Este complemento no est\u00e1 activo. La persona titular de la cuenta puede activarlo en Plan y facturaci\u00f3n.',
    'errors.network_error': 'Sin conexi\u00f3n. Revisa tu red e int\u00e9ntalo de nuevo.',
    'errors.support_code': 'C\u00f3digo de soporte: {{id}}',
    'errors.support_code_label': 'C\u00f3digo de soporte',
    'errors.validation_summary': 'Revisa los campos marcados ({{count}})',
    'errors.field_required': 'Este campo es obligatorio.',
    'errors.field_invalid': 'Valor no v\u00e1lido.',
    'errors.retry': 'Reintentar',
    'errors.copy': 'Copiar',
    'errors.copied': 'Copiado',
    'errors.invalid_credentials': 'El correo o la contrase\u00f1a no son correctos. Rev\u00edsalos y vuelve a intentarlo.',
    'errors.rate_limited': 'Hiciste muchos intentos seguidos. Espera {{retry_after}} y vuelve a intentarlo.',
    'errors.rate_limited_generic': 'Hiciste muchos intentos seguidos. Espera unos minutos y vuelve a intentarlo.',
    'errors.field_generic': 'este campo',
    'errors.not_found': 'No encontramos lo que buscas o no tienes acceso.',
    'errors.unit_second_one': '{{count}} segundo',
    'errors.unit_second_other': '{{count}} segundos',
    'errors.unit_minute_one': '{{count}} minuto',
    'errors.unit_minute_other': '{{count}} minutos',
    'errors.unit_hour_one': '{{count}} hora',
    'errors.unit_hour_other': '{{count}} horas',
  },
  en: {
    'errors.unknown': 'Something went wrong. Try again; if it keeps happening, contact support.',
    'errors.endpoint_gone': 'This feature is no longer available.',
    'errors.upload_session_expired': 'Upload session expired; upload the file again.',
    'errors.upload_init_failed': "Couldn't start the upload (HTTP {{status}})",
    'errors.upload_assembly_failed': "The server couldn't finish the upload (HTTP {{status}})",
    'errors.addon_not_active_generic': "This add-on isn't active. The account owner can turn it on in Plan & billing.",
    'errors.network_error': 'No connection. Check your network and try again.',
    'errors.support_code': 'Support code: {{id}}',
    'errors.support_code_label': 'Support code',
    'errors.validation_summary': 'Check the highlighted fields ({{count}})',
    'errors.field_required': 'This field is required.',
    'errors.field_invalid': 'Invalid value.',
    'errors.retry': 'Retry',
    'errors.copy': 'Copy',
    'errors.copied': 'Copied',
    'errors.invalid_credentials': "The email or password isn't correct. Check them and try again.",
    'errors.rate_limited': 'Too many attempts in a row. Wait {{retry_after}} and try again.',
    'errors.rate_limited_generic': 'Too many attempts in a row. Wait a few minutes and try again.',
    'errors.field_generic': 'this field',
    'errors.not_found': "We couldn't find what you're looking for, or you don't have access to it.",
    'errors.unit_second_one': '{{count}} second',
    'errors.unit_second_other': '{{count}} seconds',
    'errors.unit_minute_one': '{{count}} minute',
    'errors.unit_minute_other': '{{count}} minutes',
    'errors.unit_hour_one': '{{count}} hour',
    'errors.unit_hour_other': '{{count}} hours',
  },
};

let langGetter: () => string | undefined = () => undefined;
/** apiClient lo configura con i18n.language. */
export function configureErrorLocale(getLang: () => string | undefined): void {
  langGetter = getLang;
}
const lang = (): 'es' | 'en' => {
  try {
    const l = langGetter() ?? (typeof navigator !== 'undefined' ? navigator.language : 'es');
    return String(l || 'es').toLowerCase().startsWith('en') ? 'en' : 'es';
  } catch {
    return 'es';
  }
};

type Loose = Record<string, unknown>;
const isObj = (v: unknown): v is Loose => !!v && typeof v === 'object' && !Array.isArray(v);
const isBareCode = (s: string) => CODE_RE.test(s);
const interpolate = (s: string, p: Loose) => s.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (p[k] == null ? '' : String(p[k])));

/** Texto plano, una linea, sin markup, con tope de longitud. Todo lo que sale de este modulo pasa por aqui. */
export function toPlainText(v: unknown, max = MAX_ERROR_MESSAGE_LENGTH): string | undefined {
  if (typeof v !== 'string') return undefined;
  // eslint-disable-next-line no-control-regex
  let s = v.replace(/<[^>]*>/g, ' ').replace(/[<>]/g, ' ').replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!s || s.includes(OBJ_STR)) return undefined;
  if (s.length > max) s = s.slice(0, max - 1).trimEnd() + '\u2026';
  return s;
}

/** Texto i18n de una clave propia (con default es/en). */
export function errorText(key: string, params: Loose = {}, t?: TLike): string {
  const def = DEFAULTS[lang()][key] ?? DEFAULTS.es[key] ?? '';
  let out: unknown;
  try {
    out = t ? t(key, { ns: ERRORS_NS, ...params, defaultValue: def }) : undefined;
  } catch {
    out = undefined;
  }
  const s = interpolate(typeof out === 'string' && out && out !== key && !out.endsWith(key) ? out : def, params);
  return toPlainText(s) ?? '';
}

/** Codigos retirados del catalogo: nunca se traducen (caen a fallback + (CODE)). */
export const DEPRECATED_ERROR_CODES: readonly string[] = [];
/** Codigos que nunca se muestran al usuario (errores de integracion). */
export const HIDDEN_ERROR_CODES: readonly string[] = ['WEBHOOK_INVALID'];

/** Traduce un codigo del catalogo. undefined si no hay traduccion (nunca devuelve el codigo crudo). */
export function translateErrorCode(code: unknown, params: ErrorParams | undefined, t?: TLike): string | undefined {
  if (!t || typeof code !== 'string' || !isBareCode(code)) return undefined;
  if (DEPRECATED_ERROR_CODES.includes(code) || HIDDEN_ERROR_CODES.includes(code)) return undefined;
  // RATE_LIMITED sin retry_after: texto sin cifra (evita "Espera  y vuelve...").
  if (code === 'RATE_LIMITED' && !(params && params.retry_after)) return errorText('errors.rate_limited_generic', {}, t) || undefined;
  // ADDON_NOT_ACTIVE sin params.addon: texto generico ("este complemento"), nunca hueco ni {{addon}} literal.
  if (code === 'ADDON_NOT_ACTIVE' && !(params && params.addon)) return errorText('errors.addon_not_active_generic', {}, t) || undefined;
  const key = `errors.${code.toLowerCase()}`;
  try {
    const out = t(key, { ns: ERRORS_NS, ...prepareErrorParams(code, params, t), defaultValue: '' });
    const s = toPlainText(out);
    return s && s !== key && !s.endsWith(key) ? s : undefined;
  } catch {
    return undefined;
  }
}

// ---------- registro de codigos faltantes (solo dev, solo el codigo) ----------
const missingLogged = new Set<string>();
const isDev = (): boolean => {
  try {
    return !!(import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV;
  } catch {
    return false;
  }
};
let missingSink: (code: string) => void = (code) => {
  if (isDev()) console.debug('[errors] missing i18n for code:');
};
/** Solo para tests. */
export function setMissingCodeSink(fn: (code: string) => void): void {
  missingSink = fn;
  missingLogged.clear();
}
const reportMissing = (code: string) => {
  if (missingLogged.has(code)) return;
  missingLogged.add(code);
  try {
    missingSink(code);
  } catch { /* noop */ }
};

// ---------- extraccion (sin mutar) ----------
type Extract = {
  status?: number;
  code?: string;
  params: ErrorParams;
  obj?: Loose;
  list?: unknown[];
  rawMessage?: string;
  requestId?: string;
  retryAfter?: number;
  network: boolean;
};
type ErrLike = {
  response?: { status?: number; data?: unknown; headers?: unknown };
  code?: unknown;
  message?: unknown;
  errorCode?: string;
  __detailNormalized?: boolean;
};

const stash = new WeakMap<object, Extract>();

const headerGet = (headers: unknown, name: string): unknown => {
  if (!headers || typeof headers !== 'object') return undefined;
  const h = headers as { get?: (n: string) => unknown } & Loose;
  try {
    if (typeof h.get === 'function') {
      const v = h.get(name);
      if (v != null) return v;
    }
  } catch { /* noop */ }
  const k = Object.keys(h).find((x) => x.toLowerCase() === name.toLowerCase());
  return k ? h[k] : undefined;
};
const cleanReqId = (v: unknown): string | undefined => {
  // Numeros / cadenas numericas (hash, -5324, 8812734) nunca son codigo de soporte.
  const s = typeof v === 'string' ? v.trim() : undefined;
  return s && REQ_ID_RE.test(s) && !/^-?\d+$/.test(s) ? s : undefined;
};
/** body.request_id top-level (contrato fix/request-id-uuid): solo UUID. */
const bodyReqId = (v: unknown): string | undefined => {
  const s = typeof v === 'string' ? v.trim() : undefined;
  return s && UUID_RE.test(s) ? s : undefined;
};
/** "Mensaje. Codigo de soporte: X": agrega punto si el mensaje no cierra con puntuacion. */
export function withSupportCode(msg: string, sc: string): string {
  if (!sc) return msg;
  if (!msg) return sc;
  return /[.!?:;\u2026]$/.test(msg) ? `${msg} ${sc}` : `${msg}. ${sc}`;
}

const headerReqId = (v: unknown): string | undefined => {
  const s = typeof v === 'string' ? v.trim() : undefined;
  return s && UUID_RE.test(s) ? s : undefined;
};

function findRequestId(data: Loose | undefined, obj: Loose | undefined, params: Loose, headers: unknown): string | undefined {
  const info = (o: unknown) => (isObj(o) && isObj(o.error_info) ? o.error_info : undefined);
  // Contrato Backend (fix/request-id-uuid): body.request_id (top-level) || header X-Request-ID (mismo UUID, igual
  // que logs). Header solo si es UUID (nunca el hash numerico). Fuentes anidadas (detail / error_info / params en 5xx)
  // quedan como respaldo legado; solo se LEEN, los params del catalogo no se modifican.
  return (
    bodyReqId(data?.request_id) ??
    headerReqId(headerGet(headers, 'x-request-id')) ??
    cleanReqId(obj?.request_id) ??
    cleanReqId(info(data)?.request_id) ??
    cleanReqId(info(obj)?.request_id) ??
    cleanReqId(params.request_id) ??
    cleanReqId(info(params)?.request_id)
  );
}

const isNetworkError = (err: unknown): boolean => {
  if (err instanceof TypeError) return true; // fetch: Failed to fetch
  const e = err as ErrLike;
  if (!e || typeof e !== 'object') return false;
  if (e.response) return false;
  return e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED' || e.message === 'Network Error';
};

function extract(err: unknown): Extract {
  if (err && typeof err === 'object') {
    const s = stash.get(err as object);
    if (s) return s;
  }
  const e = err as ErrLike;
  const res = e && typeof e === 'object' ? e.response : undefined;
  const status = typeof res?.status === 'number' ? res.status : undefined;
  const data = isObj(res?.data) ? res!.data : undefined;
  const d = data?.detail;
  const obj = isObj(d) ? d : undefined;
  // Opcion A (feat/4xx-error-codes): detail sigue string y el codigo viene en data.error_info {code,message,params}.
  const info = data && isObj(data.error_info) ? data.error_info : undefined;
  const params: ErrorParams =
    obj && isObj(obj.params) ? obj.params : info && isObj(info.params) ? info.params : data && isObj(data.params) ? data.params : {};
  let code: string | undefined;
  if (obj && typeof obj.code === 'string' && isBareCode(obj.code)) code = obj.code;
  else if (info && typeof info.code === 'string' && isBareCode(info.code)) code = info.code;
  else if (data && typeof data.code === 'string' && isBareCode(data.code)) code = data.code;
  else if (typeof d === 'string' && isBareCode(d.trim())) code = d.trim();
  else if (typeof e?.errorCode === 'string') code = e.errorCode;
  let list: unknown[] | undefined = Array.isArray(d) ? d : undefined;
  if (!list && Array.isArray(params.errors)) list = params.errors as unknown[];
  const rawMessage =
    obj && typeof obj.message === 'string' ? obj.message : typeof d === 'string' && !isBareCode(d.trim()) ? d : undefined;
  return {
    status,
    code,
    params,
    obj,
    list,
    rawMessage,
    requestId: findRequestId(data, obj, params, res?.headers),
    retryAfter: parseRetryAfter(params, res?.headers),
    network: !res && isNetworkError(err),
  };
}

// ---------- 429 / retry_after ----------
const MAX_RETRY_AFTER = 7 * 24 * 3600;
/** Segundos desde params.retry_after (catalogo RATE_LIMITED) o header Retry-After (segundos o fecha HTTP). */
function parseRetryAfter(params: Loose, headers: unknown, now = Date.now()): number | undefined {
  const fromNum = (v: unknown): number | undefined => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && /^\s*\d+(\.\d+)?\s*$/.test(v) ? Number(v) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.min(Math.ceil(n), MAX_RETRY_AFTER) : undefined;
  };
  const p = fromNum(params.retry_after);
  if (p) return p;
  const h = headerGet(headers, 'retry-after');
  const hn = fromNum(h);
  if (hn) return hn;
  if (typeof h === 'string') {
    const at = Date.parse(h);
    if (Number.isFinite(at) && at > now) return Math.min(Math.ceil((at - now) / 1000), MAX_RETRY_AFTER);
  }
  return undefined;
}

/** "5 minutos" / "1 hour": unidad legible (segundos < 60, minutos < 1 h, si no horas), redondeando hacia arriba. */
export function formatRetryAfter(seconds: number, t?: TLike): string {
  const s = Math.max(1, Math.ceil(seconds));
  const [unit, count] = s < 60 ? ['second', s] : s < 3600 ? ['minute', Math.ceil(s / 60)] : ['hour', Math.ceil(s / 3600)];
  return errorText(`errors.unit_${unit}_${count === 1 ? 'one' : 'other'}`, { count }, t);
}

export function getRetryAfterSeconds(err: unknown): number | undefined {
  return extract(err).retryAfter;
}

/** Texto del 429 (mismo formateador que el catalogo RATE_LIMITED): con retry_after -> "Espera 5 minutos y vuelve a
 *  intentarlo"; sin el -> errors.rate_limited_generic. */
export function rateLimitMessage(err: unknown, t?: TLike): string {
  const secs = getRetryAfterSeconds(err);
  if (!secs) return errorText('errors.rate_limited_generic', {}, t);
  return errorText('errors.rate_limited', { retry_after: formatRetryAfter(secs, t) }, t);
}

// ---------- params del catalogo (feat/4xx-error-codes) ----------
const FIELD_RE = /^[a-z][a-z0-9_]{0,60}$/;
/** Nombre de campo de la API (snake_case) -> etiqueta i18n (errors.fields.<campo>); si no hay, "este campo". */
export function fieldLabel(field: unknown, t?: TLike): string {
  const generic = errorText('errors.field_generic', {}, t);
  if (typeof field !== 'string' || !FIELD_RE.test(field) || !t) return generic;
  try {
    const out = t(`errors.fields.${field}`, { ns: ERRORS_NS, defaultValue: '' });
    const s = toPlainText(out, 80);
    return s && !s.endsWith(`fields.${field}`) ? s : generic;
  } catch {
    return generic;
  }
}

const REQ_PATTERNS: Array<[RegExp, (m: RegExpMatchArray) => [string, Loose]]> = [
  [/^"(.{1,120})" requires (\d{1,3}) more photo\(s\)$/, (m) => [`errors.task_requirements.photos_${m[2] === '1' ? 'one' : 'other'}`, { item: m[1], count: Number(m[2]) }]],
  [/^"(.{1,120})" requires at least 1 comment$/, (m) => ['errors.task_requirements.comment', { item: m[1] }]],
  [/^"(.{1,120})" is required but not completed$/, (m) => ['errors.task_requirements.item', { item: m[1] }]],
];
/** TASK_REQUIREMENTS_MISSING.missing (ingles, "; "-separado) -> lista traducida. El titulo del checklist se conserva. */
export function translateMissingRequirements(missing: unknown, t?: TLike): string {
  const items = (Array.isArray(missing) ? missing : typeof missing === 'string' ? missing.split(/;\s*/) : [])
    .map((x) => (typeof x === 'string' ? x.trim() : ''))
    .filter(Boolean)
    .slice(0, 5);
  const out: string[] = [];
  let other = false;
  for (const it of items) {
    const hit = REQ_PATTERNS.map(([re, f]) => { const m = it.match(re); return m ? f(m) : undefined; }).find(Boolean);
    if (!hit) { other = true; continue; }
    const item = toPlainText(hit[1].item, 120) ?? '';
    out.push(t ? String(t(hit[0], { ns: ERRORS_NS, ...hit[1], item, defaultValue: '' }) || '') : '');
  }
  const clean = out.map((x) => toPlainText(x) ?? '').filter(Boolean);
  if (other || !clean.length) clean.push(t ? String(t('errors.task_requirements.other', { ns: ERRORS_NS, defaultValue: '' }) || '') : '');
  return clean.filter(Boolean).join('; ');
}

/** Prepara params para interpolar: nunca texto del backend sin mapear (salvo los que el catalogo ya da como texto: max, allowed, count, min). */
export function prepareErrorParams(code: string, params: ErrorParams | undefined, t?: TLike): ErrorParams {
  const p: Loose = { ...(params ?? {}) };
  if ('retry_after' in p) {
    const n = typeof p.retry_after === 'number' ? p.retry_after : Number(p.retry_after);
    if (Number.isFinite(n) && n > 0) p.retry_after = formatRetryAfter(n, t);
    else delete p.retry_after;
  }
  for (const k of ['field', 'field_start', 'field_end']) if (k in p) p[k] = fieldLabel(p[k], t);
  if (code === 'TASK_REQUIREMENTS_MISSING') p.missing = translateMissingRequirements(p.missing, t);
  // CARD_DECLINED: el backend manda el codigo crudo de Stripe (p. ej. "card_declined"); nunca se pinta tal cual.
  if (code === 'CARD_DECLINED') p.reason = cardDeclineReason(p.reason, t);
  // ADDON_*: clave interna (smart_locks) -> nombre legible (enums.addon.*); si no hay traduccion, sin guiones bajos.
  if (/^ADDON_/.test(code) && typeof p.addon === 'string' && p.addon) {
    const raw = p.addon;
    const name = /^[a-z0-9_]{1,40}$/.test(raw) ? errorText(`enums.addon.${raw}`, {}, t) : '';
    p.addon = name || raw.replace(/_/g, ' ');
  }
  for (const k of Object.keys(p)) {
    const v = p[k];
    if (typeof v === 'string') p[k] = toPlainText(v, 200) ?? '';
    else if (typeof v !== 'number') delete p[k];
  }
  return p;
}

// ---------- 422 ----------
/** { campo: mensaje i18n } a partir de la lista FastAPI/pydantic. Nunca usa el msg del backend. */
function fieldErrorsFrom(list: unknown[] | undefined, t?: TLike): Record<string, string> {
  const out: Record<string, string> = {};
  if (!list) return out;
  for (const it of list) {
    if (!isObj(it)) continue;
    const loc = Array.isArray(it.loc) ? it.loc.filter((x) => typeof x === 'string' && x !== 'body' && x !== 'query') : [];
    const field = typeof it.field === 'string' ? it.field : loc.length ? String(loc[loc.length - 1]) : '';
    if (!field || !/^[A-Za-z0-9_.-]{1,80}$/.test(field) || out[field]) continue;
    const type = typeof it.type === 'string' ? it.type : '';
    const code = typeof it.code === 'string' && isBareCode(it.code) ? it.code : undefined;
    out[field] =
      (code && translateErrorCode(code, isObj(it.params) ? it.params : undefined, t)) ||
      errorText(type === 'missing' || type.endsWith('.missing') || type === 'value_error.missing' ? 'errors.field_required' : 'errors.field_invalid', {}, t);
  }
  return out;
}

function resolve(x: Extract, fallback: string, t?: TLike): ErrorInfo {
  const fb = toPlainText(fallback) ?? '';
  const unknownTxt = errorText('errors.unknown', {}, t);
  const base = { status: x.status, code: x.code, requestId: x.requestId, fieldErrors: {} as Record<string, string> };
  if (x.network) return { ...base, kind: 'network', message: errorText('errors.network_error', {}, t) || fb || unknownTxt };
  const server = (x.status !== undefined && x.status >= 500) || x.code === 'INTERNAL_ERROR';
  if (server) {
    const msg = fb || unknownTxt;
    const sc = x.requestId ? errorText('errors.support_code', { id: x.requestId }, t) : '';
    return { ...base, kind: 'server', message: toPlainText(withSupportCode(msg, sc)) ?? unknownTxt };
  }
  const fieldErrors = fieldErrorsFrom(x.list, t);
  const n = Object.keys(fieldErrors).length;
  // 422 con codigo del catalogo y sin errores por campo (p. ej. PRICE_NOT_FOUND): texto del catalogo.
  if (!n && x.code && !x.list) {
    const tr = translateErrorCode(x.code, x.params, t);
    if (tr) return { ...base, kind: 'client', message: tr };
  }
  if (x.list || x.status === 422) {
    return {
      ...base,
      kind: 'validation',
      fieldErrors,
      message: n ? errorText('errors.validation_summary', { count: n }, t) : fb || unknownTxt,
    };
  }
  const kind: ErrorKind = x.status !== undefined && x.status >= 400 ? 'client' : 'unknown';
  if (x.code) {
    const tr = translateErrorCode(x.code, x.params, t);
    if (tr) return { ...base, kind, message: tr };
    // ENDPOINT_GONE (exacto): texto amable aunque el catalogo cargado no lo tenga (default interno).
    if (x.code === 'ENDPOINT_GONE') return { ...base, kind, message: errorText('errors.endpoint_gone', {}, t) || fb || unknownTxt };
    reportMissing(x.code);
    // CARD_* sin traduccion propia: rechazo amable generico, nunca "(CARD_...)" crudo.
    if (/^CARD_/.test(x.code)) {
      const card = errorText('errors.card_generic_decline', {}, t);
      if (card) return { ...base, kind, message: card };
    }
    // 404: texto uniforme NOT_FOUND (no revela si existe o si falta acceso).
    if (x.status === 404) return { ...base, kind, message: errorText('errors.not_found', {}, t) };
    // Codigo desconocido: fallback de pantalla (o errors.unknown) + codigo de soporte (request_id) si lo hay.
    // Nunca el codigo crudo entre parentesis (QA P3).
    const sc = x.requestId ? errorText('errors.support_code', { id: x.requestId }, t) : '';
    const msg = fb || unknownTxt;
    return { ...base, kind, message: toPlainText(withSupportCode(msg, sc)) ?? unknownTxt };
  }
  if (x.status === 404) return { ...base, kind, message: errorText('errors.not_found', {}, t) };
  // 410 sin codigo (p. ej. sesion de subida de evidencia expirada): gana el mensaje de la pantalla;
  // errors.endpoint_gone solo como ultimo recurso. ENDPOINT_GONE con codigo sale del catalogo.
  if (x.status === 410) return { ...base, kind, message: fb || errorText('errors.endpoint_gone', {}, t) || unknownTxt };
  return { ...base, kind, message: fb || unknownTxt };
}

// ---------- API publica ----------
/**
 * Normaliza error.response.data en sitio (idempotente). Devuelve el mismo error.
 * detail -> texto seguro | undefined; objeto/params/message crudo -> WeakMap; data.code / error.errorCode -> codigo.
 */
const hide = (o: Loose, k: string, v: unknown) => {
  if (v === undefined) { delete o[k]; return; }
  try { Object.defineProperty(o, k, { value: v, enumerable: false, configurable: true, writable: true }); } catch { delete o[k]; }
};

export function normalizeApiError<E>(error: E, t?: TLike): E {
  const e = error as unknown as ErrLike;
  if (!e || typeof e !== 'object' || e.__detailNormalized) return error;
  const x = extract(e);
  stash.set(e, x);
  const data = e.response?.data;
  if (isObj(data)) {
    stash.set(data, x);
    const info = resolve(x, '', t);
    // Los ~210 lectores hacen detail || fallback: si no hay nada especifico, undefined para que use su fallback.
    const specific = info.kind === 'server' ? !!x.requestId : info.kind === 'validation' ? Object.keys(info.fieldErrors).length > 0 : !!x.code;
    data.detail = specific ? info.message : undefined;
    if (x.code) {
      data.code = x.code;
      e.errorCode = x.code;
    }
    // params / message quedan NO enumerables: los modulos (condo, D-EM1, tests toMatchObject) siguen leyendo
    // data.params / data.message; JSON.stringify / Object.keys / spread no los copian. OJO: NO es una barrera para
    // la consola: al expandir console.log(err) en DevTools las propiedades no enumerables SI se ven. Por eso los logs
    // solo admiten textos fijos o errorStatusForLog(err) (consoleLogs.ast.test.mjs).
    const rawParams = isObj(data.params) ? data.params : isObj(data.error_info) && isObj((data.error_info as Loose).params) ? (data.error_info as Loose).params : undefined;
    hide(data, 'params', rawParams);
    if ('message' in data && typeof data.message === 'string') hide(data, 'message', data.message);
    if ('error_info' in data) delete data.error_info;

  }
  try {
    Object.defineProperty(e, '__detailNormalized', { value: true, enumerable: false });
  } catch { /* objeto congelado */ }
  return error;
}

/** Informacion completa para UI (mensaje, tipo, codigo, request_id, errores por campo). */
export function getErrorInfo(err: unknown, fallback = '', t?: TLike): ErrorInfo {
  return resolve(extract(err), fallback, t);
}

/** Mensaje para el usuario (ver reglas arriba). */
export function getErrorMessage(err: unknown, fallback: string, t?: TLike): string {
  return getErrorInfo(err, fallback, t).message;
}

type BodyOpts = { status?: number; headers?: unknown };
const bodyErr = (body: unknown, opts?: BodyOpts) => ({ response: { status: opts?.status, data: body, headers: opts?.headers } });

/** Igual que getErrorMessage pero sobre un cuerpo ya leido (fetch / XHR). Pasar status y headers si se tienen. */
export function getBodyErrorMessage(body: unknown, fallback: string, t?: TLike, opts?: BodyOpts): string {
  return getErrorMessage(bodyErr(body, opts), fallback, t);
}
export function getBodyErrorInfo(body: unknown, fallback: string, t?: TLike, opts?: BodyOpts): ErrorInfo {
  return getErrorInfo(bodyErr(body, opts), fallback, t);
}

/** { campo: mensaje i18n } para 422. */
export function getFieldErrors(err: unknown, t?: TLike): Record<string, string> {
  return getErrorInfo(err, '', t).fieldErrors;
}

/** Codigo del catalogo (p. ej. 'INVALID_TRANSITION'). Para logica; mostrarlo solo via getErrorMessage. */
export function getErrorCode(err: unknown): string | undefined {
  return extract(err).code;
}

/** Params del catalogo ({} si no hay). Solo para logica: nunca a consola ni analytics. */
export function getErrorParams(err: unknown): ErrorParams {
  return extract(err).params;
}

export function getRequestId(err: unknown): string | undefined {
  return extract(err).requestId;
}

/** Detalle estructurado con params aplanados ({ ...params, ...detail }) para lectores legacy. Solo para logica. */
export function getErrorDetail(err: unknown): Loose | undefined {
  const x = extract(err);
  if (!x.obj) return undefined;
  return { ...x.params, ...x.obj };
}

/** Mensaje crudo del backend, SOLO para comparaciones heredadas tipo includes('...'). Nunca mostrar ni loguear. */
export function getErrorRawMessage(err: unknown): string {
  return extract(err).rawMessage ?? '';
}

/** Lo unico que se loguea de un error: status (o 'network'). */
export function errorStatusForLog(err: unknown): string {
  const x = extract(err);
  if (x.status !== undefined) return `status=${x.status}`;
  if (x.network) return 'status=network';
  return 'status=unknown';
}

const BLOCKED_KEYS = /^(detail_obj|detail|errorParams|error_params|params|response|config|request|stack|error_info|raw.*)$/i;
/** Limpia un evento antes de dataLayer: sin objetos de error, sin params, strings planos con tope. */
export function sanitizeForAnalytics<T extends Loose>(event: T): T {
  const out: Loose = {};
  for (const [k, v] of Object.entries(event)) {
    if (BLOCKED_KEYS.test(k)) continue;
    if (v == null || typeof v === 'number' || typeof v === 'boolean') out[k] = v;
    else if (typeof v === 'string') out[k] = toPlainText(v, 200) ?? '';
    else if (v instanceof Error || (isObj(v) && ('response' in v || 'isAxiosError' in v))) out[k] = errorStatusForLog(v);
    else if (Array.isArray(v)) out[k] = v.filter((y) => typeof y === 'string' || typeof y === 'number').slice(0, 20);
    else if (isObj(v)) out[k] = sanitizeForAnalytics(v);
  }
  return out as T;
}

/** Interceptor de respuesta para cualquier instancia axios: normaliza y vuelve a rechazar. */
export function installErrorDetailNormalizer(
  instance: { interceptors: { response: { use: (ok: (r: unknown) => unknown, ko: (e: unknown) => unknown) => number } } },
  getT?: () => TLike | undefined,
): number {
  return instance.interceptors.response.use(
    (r) => r,
    (error) => Promise.reject(normalizeApiError(error, getT?.())),
  );
}

/** Codigo de rechazo de Stripe -> texto de enums.card_decline (generic si no se conoce). */
const STRIPE_DECLINE_ALIASES: Record<string, string> = {
  insufficient_funds: 'insufficient_funds',
  expired_card: 'expired_card',
  incorrect_cvc: 'incorrect_details',
  incorrect_number: 'incorrect_details',
  invalid_cvc: 'incorrect_details',
  invalid_number: 'incorrect_details',
  invalid_expiry_month: 'incorrect_details',
  invalid_expiry_year: 'incorrect_details',
  incorrect_details: 'incorrect_details',
};
export function cardDeclineReason(raw: unknown, t?: TLike): string {
  const k = typeof raw === 'string' ? STRIPE_DECLINE_ALIASES[raw] ?? 'generic' : 'generic';
  return errorText(`enums.card_decline.${k}`, {}, t) || errorText('enums.card_decline.generic', {}, t) || '';
}

/**
 * Mensaje de error al validar un cupon (Onboarding y BillingPage): 5xx / INTERNAL_ERROR -> "servicio de pagos
 * no disponible" + codigo de soporte; nunca "cupon invalido". 4xx: catalogo (COUPON_INVALID, COUPON_EXPIRED, ...).
 */
export function couponValidateErrorMessage(err: unknown, t?: TLike): string {
  const x = extract(err);
  if (x.code === 'INTERNAL_ERROR' || (x.status !== undefined && x.status >= 500)) {
    const sc = x.requestId ? errorText('errors.support_code', { id: x.requestId }, t) : '';
    return withSupportCode(errorText('errors.payments_unavailable', {}, t), sc);
  }
  if (x.status === 429 || x.code === 'RATE_LIMITED') return rateLimitMessage(err, t);
  return getErrorMessage(err, errorText('errors.coupon_invalid', {}, t), t);
}

/** Mensaje de pantalla para errores de subida reanudable (init / complete): 410 sin codigo = sesion de subida
 *  vencida (evidence.py), nunca "funcion no disponible". Otros status: '' (cae a catalogo / errors.unknown). */
export function uploadScreenFallback(status: number | undefined, t?: TLike): string {
  return status === 410 ? errorText('errors.upload_session_expired', {}, t) : '';
}

/** Mensaje de error de /init de subida reanudable (ya traducido, sin prefijos en ingles): 410 -> sesion vencida;
 *  si hay mensaje del catalogo / pantalla, ese; si no, errors.upload_init_failed (HTTP n). */
export function uploadInitErrorMessage(status: number, body: unknown, t?: TLike, headers?: unknown): string {
  const screen = uploadScreenFallback(status, t);
  const detail = body === undefined ? screen : getBodyErrorMessage(body, screen, t, { status, headers } as BodyOpts);
  return detail || errorText('errors.upload_init_failed', { status }, t);
}
